// Espaciado horizontal único de las tablas del portal.
//
// Antes de esta constante convivían tres valores heredados literal del
// prototipo para lo mismo: 24px (default de `CursorTable`, valor fijo de
// `ListingTablePanel`, `accessLogStyles.table`), 22px (`catalogStyles.table`)
// y 20px (`catalogStyles.workshopTable`) — los dos últimos dentro de la misma
// pantalla de Catálogos, con el encabezado a 22px y las filas a 20px, o sea
// desalineados entre sí. Lo mismo pasaba con la separación entre columnas:
// "14px" en Bitácora/Consumos/Gran premio·Avance, "12px" en Motor de Puntos,
// "0" en Usuarios y el default "0" que heredaba Perfiles sin pasarlo.
//
// Gana 24px / 14px: es el valor mayoritario en el prototipo (admin.jsx:3605
// SegUsuarios, :3720 SegBitacora, :3390 SegPerfiles usan `padding '18px 24px
// 11px'` / `'12px 24px'` con `gap: 14`), así que converger no inventa un valor
// nuevo — alinea a las demás las tres tablas que se habían desviado.
//
// Vive acá y no en `@core/theme/tokens`: DESIGN-TOKENS.md solo define
// color/radio/sombra/fuente, no una escala de espaciado (mismo criterio que
// `catalogStyles.ts` y `profileStyles.ts` documentan).
export const tableSpacing = {
  /** Padding horizontal de encabezado y filas. El mismo para los dos: si
   *  divergen, las columnas del header dejan de alinearse con las de la fila. */
  inlinePadding: "24px",
  /** Separación entre columnas de la grilla (`column-gap`). */
  columnGap: "14px",
  /** Separación entre los botones de acción de una fila. */
  actionGap: 6,
  /**
   * Padding único del botón de acción de fila (`TableRowActionButton`).
   * Antes convivían cuatro tamaños para el mismo botón: el `.chip` base
   * (9px 15px) en Catálogos y Perfiles, "5px 6px" con fuente 10px en
   * Usuarios, "7px 11px" en Círculo de miembros y Vehículos, y en Motor de
   * Puntos el valor lo pasaba quien llamaba. Gana el del prototipo
   * (admin.jsx:3628-3630 SegUsuarios), que además coincide salvo 1px con el
   * ya compartido `RowEditButton` de `@portal/common`.
   */
  actionPadding: "7px 11px",
  /**
   * Tamaño del ícono de un botón de acción de fila que muestra SOLO ícono
   * (`variant: "icon"`), sin texto. 16px es el mismo cuerpo que usa el resto
   * del portal para un glifo embebido en una caja de control.
   */
  iconActionSize: 16,
  /**
   * Padding del botón de acción de fila SOLO ícono. No es `actionPadding`
   * a propósito y tampoco es un valor a ojo: se elige para que la caja mida
   * exactamente lo mismo que la del botón con texto, porque las dos formas
   * conviven en la misma fila y 2px de diferencia de alto se ven como un
   * escalón. Con texto la línea mide 12px (`.chip` fija `font: … 12px/1`) y
   * el padding vertical es 7 → 7+12+7 = 26. Con ícono la línea mide 16 (el
   * `<svg>` es el ítem flex más alto) → 5+16+5 = 26. Mismo alto, y el
   * horizontal se mantiene en 11 para que el borde redondeado sea idéntico.
   */
  iconActionPadding: "5px 11px",
} as const;

/**
 * Compone el `padding` de una fila o encabezado de tabla respetando el
 * espaciado horizontal único: solo se elige el aire vertical.
 *
 * `tablePadding("12px")`          → "12px 24px"
 * `tablePadding("18px", "11px")`  → "18px 24px 11px"
 */
export function tablePadding(blockStart: string, blockEnd?: string): string {
  return blockEnd === undefined || blockEnd === blockStart
    ? `${blockStart} ${tableSpacing.inlinePadding}`
    : `${blockStart} ${tableSpacing.inlinePadding} ${blockEnd}`;
}
